import React from 'react';
import '../styles.scss';
import { configData } from "../config.js";
import { fetchSigned } from '../shared/Utilities';

import {
  OwcLogo, OwcIconButton, OwcTypography, OwcModalDialog, OwcSwitch,
} from '@one/react';
import GenericDataProduct from "../components/dataProduct/GenericDataProduct.js";
import CountryReport from "../components/dataProduct/CountryReport/CountryReport.js";


/** 
 * @copyright Roche 2022
 * @author Nick Draper
 */

/**
 * Data Product Query component
 */
class DataProduct extends React.Component {

  NO_DATA_PRODUCT_SELECTED = "Select a data product";

  constructor(props) {
    super(props);
    const selectedDataProduct = this.NO_DATA_PRODUCT_SELECTED;
    this.state = {
      loadingDataProducts: true,
      selectedDataProduct: selectedDataProduct,
      displayGenericDataProduct: false,
      allowGenericDPSelection: false,
      groups: props.groups,
      dataProducts: {},
      showAbout: false,
      waiting: true,
    }
  }

  /**
   * Simple delay function
   * @param {*} milliseconds The number of seconds to wait for
   * @returns A promise that will resolve after the delay
   */
  sleep = (milliseconds) => {
    return new Promise(resolve => setTimeout(resolve, milliseconds))
  }

  getDataProducts(groups) {
    try {
      fetchSigned(configData.DATA_PRODUCT_URL + "/data/list/")
        .then(async response => {
          let responseDict = {};
          if (response !== undefined) {
            const contentType = response.headers.get("content-type");
            if (contentType && contentType.indexOf("application/json") !== -1) {
              responseDict = await response.json();
            } else {
              console.log(response)
            }
          }
          console.log(`Found data products: ${JSON.stringify(responseDict)}`)

          // for each data product check this user can access it
          const entries_to_remove = [];
          for (const [name, endpoint_suffix] of Object.entries(responseDict)) {
            const url = configData.DATA_PRODUCT_URL + endpoint_suffix + "test-access/";
            try {
              const responseDP = await fetchSigned(url, {
                method: 'POST',
                headers: {
                  Accept: 'application/json',
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({groups: groups})
              });
              if ((responseDP === undefined) ||
                  (!(responseDP.ok && (responseDP.status === 200)))) {
                throw new Error("Data product retrieval failed.");
              } else {
                const body = await responseDP.json();
                if (body.authorized !== true) {
                  console.log(`Data product retrieval was disallowed - body.authorized = ${body.authorized}`);
                  throw new Error("Data product retrieval was disallowed.");
                }
              }
            } catch (e) {
              console.log(`Error was encountered when fetching url ${url} - ${e}`);
              entries_to_remove.push(name);
            }
          }

          for (const name of entries_to_remove) {
              delete responseDict[name];
          }

          this.setState({
            dataProducts: responseDict,
            loadingDataProducts: false
          });
        })
        .catch(error => {
          this.setState({
            responseText: error.toString(),
            loadingDataProducts: false
          });
          console.error('There was an error!', error);
        });
    } catch (e) {
      console.log(`Unexpected error occurred: ${e}`);
      this.setState({
        waiting: false,
        groups: null
      });
    };
  }

  getBaseURL(dataProductName) {
    let url = null;
    if (this.state.dataProducts[dataProductName] !== undefined) {
      url = configData.DATA_PRODUCT_URL + this.state.dataProducts[dataProductName]
    }
    return url;
  }

  async componentDidMount() {
    this.getDataProducts(this.props.groups);      
  }

  componentDidUpdate(prevProps) {
    if (prevProps.groups !== this.props.groups) {
      this.setState({ selectedDataProduct: this.NO_DATA_PRODUCT_SELECTED });
      this.getDataProducts(this.props.groups);
    }
  }

  /**
   * Handles changes in the data product
   * @param {*} value The name of the data product
   */
  handleSelectChange(value) {
    const allowGenericDPSelection = (value === "Country Report" ? true : false);
    this.setState({
        selectedDataProduct: value,
        allowGenericDPSelection: allowGenericDPSelection,
      }
    );
  }

  toggleAbout() {
    const isVisible = this.state.showAbout;
    this.setState({ showAbout: !isVisible })
  }

  renderDataProductSelector() {
    if (this.state.loadingDataProducts) {
      return (
        <div style={{
          display: "flex", flexDirection: "column",
          alignItems: "center"
        }}>
          <owc-progress-spinner style={{ marginTop: '30vh', marginBottom: "40vh" }} />
        </div>
      );
    } else {
      return (
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", marginTop: "1em" }}>
          <OwcIconButton id="menu_anchor" icon="menu" onClick={(ev) => this.toggleAbout(ev.detail)}  />
          <OwcTypography style={{ marginRight: "1em" }} variant="title6">Please select a Data Product:</OwcTypography>
          <select
            id={"dataProductSelect"}
            key={"dataProductSelect"}
            label="Data Product" value={this.state.selectedDataProduct}
            onChange={(ev) => this.handleSelectChange(ev.target.value)}>
            <option>{this.NO_DATA_PRODUCT_SELECTED}</option>
            {Object.entries(this.state.dataProducts).map(([key, value]) => (
              <option
                key={"dataProductOption" + key} style={{ color: "black" }}>
                {key}
              </option>
            ))}
          </select>
          {this.state.allowGenericDPSelection
            ?<OwcSwitch
                labelPlacement='left'
                checked={this.state.displayGenericDataProduct}
                onValueChange={(ev) => this.setState({ displayGenericDataProduct: ev.detail })}>
              <OwcTypography variant="caption">Enable Generic Data Product Mode</OwcTypography>
            </OwcSwitch>
            :""
          }
        </div>
      );
    }
  }

  renderAbout() {
    return (
      <OwcModalDialog
          visible={this.state.showAbout} 
          onVisibleChange={() => this.toggleAbout()}>
        <div className="about-screen-content-wrapper" 
            style={{
              backgroundColor: 'var(--one-color-white)',
              height: 'calc(100% - 80px)',
              display: 'flex',
              flexDirection: 'column',
              padding: '80px 40px',
              fontFamily: 'var(--one-text-font-family-default)'
            }}>
          <div className="title-container" 
              style={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                justifyContent: 'space-between',
                color: 'var(--one-color-background-brand-base)'
              }}>
            <span className="title" 
                style={{
                  fontWeight: 'bold',
                  fontSize: '33px',
                  lineHeight: '49px'
                }}>
              Data Products
            </span>
            <OwcLogo name="roche_logo" style={{ width: '77px', fontSize: '48px', color: 'var(--one-color-background-brand-base)'}} />
          </div>
          <span className="subtitle" 
              style={{
                fontStyle: 'italic',
                fontSize: '32px',
                lineHeight: '1',
                color: 'var(--one-color-background-brand-base)',
                margin: '8px 0'
              }}>
            About
          </span>
          <div className="content" style={{ flexGrow: 1, overflow: 'auto', margin: '48px 0 38px 0' }}>
            The application provides secure access to approved data products to the consumer.
            <br/><br/>
            On the landing page (after signing in), the consumer will be able to select the desired data product
            that they wish to request. The screen will have a pre-populated URL and metadata of the product. 
            Users can add additional parameters next to the URL to refine the data product output. 
            Kindly refer to the "How to query" section to learn more about URL queries.
            <br/><br/>
            Use the “Sign & Submit” button to execute the query and to securely view the data product. 
            The data product response can be downloaded as a file if required.
          </div>
        </div>
      </OwcModalDialog>
    );
  }

  renderDataProduct() {
    const selectedDataProduct = this.state.selectedDataProduct;
    switch(selectedDataProduct){
      case this.NO_DATA_PRODUCT_SELECTED:
        return ("");
      case CountryReport.getDataProductName():
        if (!this.state.displayGenericDataProduct) {
          return (
            <CountryReport selectedDataProduct={selectedDataProduct}
              baseUrl={this.getBaseURL(selectedDataProduct)}/>
          );
        } else {
          return (
            <GenericDataProduct selectedDataProduct={selectedDataProduct}
              baseUrl={this.getBaseURL(selectedDataProduct)}/>
          );
        }
      default:
        return (
          <GenericDataProduct selectedDataProduct={selectedDataProduct}
            baseUrl={this.getBaseURL(selectedDataProduct)}/>
        );
    }
  }

  render() {
    return (
      <div style={{ display: 'flex', width: "100%", flexDirection: "column" }}>
        {this.renderDataProductSelector()}
        {this.renderDataProduct()}
        {this.state.showAbout ? this.renderAbout() : ""}
      </div>
    );
  }

}

export default DataProduct;
